import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { GetWorkerVm } from 'api/management/get-worker.vm';
import { GetWorkersVm } from 'api/management/get-workers.vm';
import { getWorkers } from 'api/management/get-workers';

interface AddTeamMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (workerId: string) => void;
  loading?: boolean;
  excludeWorkerIds?: string[]; // Workers already in the team
}

const AddTeamMemberDialog: React.FC<AddTeamMemberDialogProps> = ({ open, onClose, onSubmit, loading = false, excludeWorkerIds = [] }) => {
  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [workers, setWorkers] = useState<GetWorkerVm[]>([]);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [workersOffset, setWorkersOffset] = useState('');
  const [hasMoreWorkers, setHasMoreWorkers] = useState(true);

  const fetchWorkers = (offset: string = '', reset: boolean = false) => {
    setWorkersLoading(true);
    getWorkers(offset, {
      success: (response: GetWorkersVm) => {
        setHasMoreWorkers(response.nextOffset !== null && response.nextOffset !== '');
        setWorkersOffset(response.nextOffset || '');
        setWorkers((prevWorkers) => {
          const newWorkers = reset ? response.objects : [...prevWorkers, ...response.objects];
          // Filter out workers already in the team
          return newWorkers.filter((worker) => !excludeWorkerIds.includes(worker.id));
        });
        setWorkersLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter operadores:', err);
        setWorkersLoading(false);
      },
    });
  };

  useEffect(() => {
    if (open) {
      fetchWorkers('', true);
      setSelectedWorkerId('');
    }
  }, [open, excludeWorkerIds]);

  const handleSubmit = () => {
    if (selectedWorkerId) {
      onSubmit(selectedWorkerId);
    }
  };

  const handleClose = () => {
    setSelectedWorkerId('');
    setWorkers([]);
    onClose();
  };

  const loadMoreWorkers = () => {
    if (hasMoreWorkers && !workersLoading) {
      fetchWorkers(workersOffset, false);
    }
  };

  const availableWorkers = workers.filter((worker) => !excludeWorkerIds.includes(worker.id));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adicionar Membro à Equipa</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {availableWorkers.length === 0 && !workersLoading ? (
            <Typography variant="body2" color="textSecondary">
              Não há operadores disponíveis para adicionar.
            </Typography>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Operador</InputLabel>
              <Select
                value={selectedWorkerId}
                label="Operador"
                onChange={(e) => setSelectedWorkerId(e.target.value)}
                disabled={loading || workersLoading}
              >
                {availableWorkers.map((worker) => (
                  <MenuItem key={worker.id} value={worker.id}>
                    {worker.name} ({worker.username})
                  </MenuItem>
                ))}
                {hasMoreWorkers && (
                  <MenuItem onClick={loadMoreWorkers} disabled={workersLoading}>
                    <em>{workersLoading ? 'A carregar...' : 'Carregar mais operadores'}</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedWorkerId || loading || availableWorkers.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'A adicionar...' : 'Adicionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTeamMemberDialog;
